// src/features/dashboard/teacher/TopicsManager.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import {
  fetchTopicsForClass,
  addTopic,
  updateTopic,
  deleteTopic,
  addTopicsBatch,
} from '../../../services/sheetDataService';
import ImportTopicsModal from './ImportTopicsModal';

// Helper: extract numeric part from a grade string (e.g., "11A" → "11")
const getNumericGrade = (grade) => {
  if (!grade) return '';
  const match = grade.match(/\d+/);
  return match ? match[0] : grade;
};

const TopicsManager = ({ classItem, spreadsheetId }) => {
  const { accessToken, user } = useAuth();

  // Key for fetching: teacher + subject only (grade is handled client-side)
  const classKey = useMemo(() => {
    if (!classItem) return '';
    const teacher = classItem.email || user.email || '';
    const subject = classItem.subject || '';
    return `${teacher}|${subject}`;
  }, [classItem?.email, classItem?.subject, user?.email]);

  // Numeric grade of the current class (used for filtering and storing)
  const numericGrade = useMemo(() => {
    return getNumericGrade(classItem?.grade);
  }, [classItem?.grade]);

  const sheetId = spreadsheetId || user.spreadsheetId;

  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const [commonFields, setCommonFields] = useState({
    term: '',
    termStart: '',
    termEnd: '',
  });

  const [topicRows, setTopicRows] = useState([
    { topic: '', subtopics: '' }
  ]);

  // Load topics – fetch all for teacher+subject, then filter by numeric grade
  useEffect(() => {
    const controller = new AbortController();

    const loadTopics = async () => {
      if (!accessToken || !sheetId || !classKey || !numericGrade) {
        setLoading(false);
        return;
      }

      const [teacher, subject] = classKey.split('|');
      if (!teacher || !subject) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Fetch ALL topics for this teacher+subject (grade filter skipped)
        const allTopics = await fetchTopicsForClass(
          accessToken,
          sheetId,
          teacher,
          subject,
          '' // empty grade => service should skip grade filter
        );
        // Filter client‑side by numeric grade
        const filtered = allTopics.filter(t => {
          const topicNumeric = getNumericGrade(t.grade);
          return topicNumeric === numericGrade;
        });
        if (!controller.signal.aborted) {
          setTopics(filtered);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Error loading topics:', err);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    setTopics([]);
    setEditingId(null);
    setTopicRows([{ topic: '', subtopics: '' }]);
    setCommonFields({ term: '', termStart: '', termEnd: '' });
    
    loadTopics();
    return () => controller.abort();
  }, [accessToken, sheetId, classKey, numericGrade]);

  const addRow = () => {
    setTopicRows([...topicRows, { topic: '', subtopics: '' }]);
  };

  const removeRow = (index) => {
    if (topicRows.length <= 1) {
      alert('You need at least one topic row.');
      return;
    }
    const newRows = topicRows.filter((_, i) => i !== index);
    setTopicRows(newRows);
  };

  const updateRow = (index, field, value) => {
    const newRows = [...topicRows];
    newRows[index][field] = value;
    setTopicRows(newRows);
  };

  const handleCommonChange = (e) => {
    setCommonFields({ ...commonFields, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validRows = topicRows.filter(row => row.topic.trim() !== '');
    if (validRows.length === 0) {
      alert('Please enter at least one topic.');
      return;
    }

    setSaving(true);

    try {
      const [teacher, subject] = classKey.split('|');

      if (editingId) {
        const rowData = topicRows[0];
        await updateTopic(accessToken, sheetId, editingId, {
          topic: rowData.topic,
          subtopics: rowData.subtopics,
          term: commonFields.term,
          termStart: commonFields.termStart,
          termEnd: commonFields.termEnd,
          // We don't update grade on edit – it stays as stored
        });
        alert('Topic updated successfully!');
      } else {
        // New topics: store with numeric grade
        const topicsToAdd = validRows.map(row => ({
          teacher,
          subject,
          grade: numericGrade, // store numeric grade (e.g., "11")
          topic: row.topic,
          subtopics: row.subtopics,
          term: commonFields.term,
          termStart: commonFields.termStart,
          termEnd: commonFields.termEnd,
        }));

        const result = await addTopicsBatch(accessToken, sheetId, topicsToAdd);

        if (result.skipped > 0) {
          alert(
            `✅ ${result.added} topics added, ⚠️ ${result.skipped} skipped (duplicates):\n${result.skippedDetails.join('\n')}`
          );
        } else {
          alert(`✅ ${result.added} topics added successfully!`);
        }
      }

      // Reset form
      setTopicRows([{ topic: '', subtopics: '' }]);
      setCommonFields({ term: '', termStart: '', termEnd: '' });
      setEditingId(null);
      
      // Refresh topics – re-fetch all and filter again
      const allTopics = await fetchTopicsForClass(accessToken, sheetId, teacher, subject, '');
      const filtered = allTopics.filter(t => getNumericGrade(t.grade) === numericGrade);
      setTopics(filtered);
    } catch (err) {
      console.error('Save error:', err);
      alert('❌ Failed to save topics: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (topic) => {
    setEditingId(topic.rowNumber);
    setCommonFields({
      term: topic.term,
      termStart: topic.termStart,
      termEnd: topic.termEnd,
    });
    setTopicRows([{ topic: topic.topic, subtopics: topic.subtopics }]);
    document.getElementById('topic-form').scrollIntoView({ behavior: 'smooth' });
  };

  const handleDelete = async (rowNumber) => {
    if (!window.confirm('Are you sure you want to delete this topic?')) return;
    try {
      await deleteTopic(accessToken, sheetId, rowNumber);
      const [teacher, subject] = classKey.split('|');
      const allTopics = await fetchTopicsForClass(accessToken, sheetId, teacher, subject, '');
      const filtered = allTopics.filter(t => getNumericGrade(t.grade) === numericGrade);
      setTopics(filtered);
    } catch (err) {
      alert('Failed to delete topic: ' + err.message);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setTopicRows([{ topic: '', subtopics: '' }]);
    setCommonFields({ term: '', termStart: '', termEnd: '' });
  };

  const handleImportComplete = async () => {
    if (classKey && numericGrade) {
      const [teacher, subject] = classKey.split('|');
      const allTopics = await fetchTopicsForClass(accessToken, sheetId, teacher, subject, '');
      const filtered = allTopics.filter(t => getNumericGrade(t.grade) === numericGrade);
      setTopics(filtered);
    }
  };

  if (loading) return <div>Loading topics...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">
          Topics – {classItem?.subject} (Grade {numericGrade})
        </h3>
        <button
          onClick={() => setShowImportModal(true)}
          className="bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 text-sm"
        >
          📥 Import Topics
        </button>
      </div>

      {/* The rest of the JSX is exactly the same as before – no changes needed */}
      <form id="topic-form" onSubmit={handleSubmit} className="mb-6 p-3 border rounded bg-gray-50">
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div>
            <label className="block text-sm font-medium">Term</label>
            <input
              type="text"
              name="term"
              value={commonFields.term}
              onChange={handleCommonChange}
              placeholder="e.g., Term 1"
              className="w-full border rounded px-2 py-1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Term Start</label>
            <input
              type="date"
              name="termStart"
              value={commonFields.termStart}
              onChange={handleCommonChange}
              className="w-full border rounded px-2 py-1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Term End</label>
            <input
              type="date"
              name="termEnd"
              value={commonFields.termEnd}
              onChange={handleCommonChange}
              className="w-full border rounded px-2 py-1"
            />
          </div>
        </div>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-medium">Topics & Subtopics</h4>
            {!editingId && (
              <button
                type="button"
                onClick={addRow}
                className="text-sm bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
              >
                + Add Row
              </button>
            )}
          </div>
          {topicRows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-5 gap-2 mb-2 items-end">
              <div className="col-span-2">
                <label className="block text-xs font-medium">Topic *</label>
                <input
                  type="text"
                  value={row.topic}
                  onChange={e => updateRow(idx, 'topic', e.target.value)}
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium">Subtopics</label>
                <input
                  type="text"
                  value={row.subtopics}
                  onChange={e => updateRow(idx, 'subtopics', e.target.value)}
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div className="flex items-end gap-1">
                {!editingId && topicRows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    className="text-red-600 hover:text-red-800 text-sm px-1 py-1"
                    title="Remove this row"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving}
            className={`bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700 ${
              saving ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {saving ? 'Saving...' : editingId ? 'Update Topic' : 'Add Topics'}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-300 px-4 py-1 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {topics.length === 0 ? (
        <p className="text-gray-500">No topics recorded for this grade.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-2 py-1 text-left">Created</th>
                <th className="border px-2 py-1 text-left">Topic</th>
                <th className="border px-2 py-1 text-left">Subtopics</th>
                <th className="border px-2 py-1 text-left">Term</th>
                <th className="border px-2 py-1 text-left">Term Start</th>
                <th className="border px-2 py-1 text-left">Term End</th>
                <th className="border px-2 py-1 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {topics.map((t) => (
                <tr key={t.rowNumber} className="border-t">
                  <td className="border px-2 py-1">{new Date(t.createdDate).toLocaleDateString()}</td>
                  <td className="border px-2 py-1">{t.topic}</td>
                  <td className="border px-2 py-1">{t.subtopics}</td>
                  <td className="border px-2 py-1">{t.term}</td>
                  <td className="border px-2 py-1">{t.termStart ? new Date(t.termStart).toLocaleDateString() : ''}</td>
                  <td className="border px-2 py-1">{t.termEnd ? new Date(t.termEnd).toLocaleDateString() : ''}</td>
                  <td className="border px-2 py-1">
                    <button
                      onClick={() => handleEdit(t)}
                      className="text-blue-600 hover:underline mr-2 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(t.rowNumber)}
                      className="text-red-600 hover:underline text-sm"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ImportTopicsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        classItem={classItem}
        spreadsheetId={sheetId}
        onImportComplete={handleImportComplete}
      />
    </div>
  );
};

export default TopicsManager;